import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoLockup } from "./Logo";
import { useAuth } from "../lib/AuthContext";

const NAV = [
  { to: "/#how", label: "How It Works" },
  { to: "/#capabilities", label: "Capabilities" },
  { to: "/#pricing", label: "Pricing" },
  { to: "/#faq", label: "FAQ" },
];

export default function MarketingLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#0F1115] flex flex-col">
      <header className="sticky top-0 z-40 bg-white border-b border-[var(--tm-border)]" data-testid="marketing-nav">
        <div className="tm-container flex items-center h-16">
          <Link to="/" className="flex items-center" data-testid="marketing-nav-logo"><LogoLockup /></Link>
          <nav className="hidden lg:flex items-center gap-8 ml-10">
            {NAV.map((n) => (
              <a key={n.label} href={n.to.replace("/", "")} className="text-sm font-medium text-[#3B424B] hover:text-[#0F1115] transition-colors" data-testid={`marketing-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </a>
            ))}
          </nav>
          <div className="ml-auto hidden lg:flex items-center gap-3">
            {user ? (
              <button className="tm-btn-outline h-10 px-4" onClick={() => nav("/app")} data-testid="marketing-nav-dashboard">Go to Dashboard</button>
            ) : (
              <>
                <Link to="/signin" className="text-sm font-medium text-[#0F1115] hover:text-[#14624A] transition-colors" data-testid="marketing-nav-signin">Sign In</Link>
                <Link to="/early-access" className="tm-btn-gold h-10 px-4" data-testid="marketing-nav-cta">Get Early Access</Link>
              </>
            )}
          </div>
          <button className="lg:hidden ml-auto p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="menu" data-testid="marketing-nav-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-[var(--tm-border)]">
            <div className="tm-container py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <a key={n.label} href={n.to.replace("/", "")} onClick={() => setOpen(false)} className="py-2 text-[15px] font-medium text-[#0F1115]">{n.label}</a>
              ))}
              {user ? (
                <button className="tm-btn-outline mt-2" onClick={() => { setOpen(false); nav("/app"); }}>Go to Dashboard</button>
              ) : (
                <>
                  <Link to="/signin" onClick={() => setOpen(false)} className="py-2 text-[15px] font-medium">Sign In</Link>
                  <Link to="/early-access" onClick={() => setOpen(false)} className="tm-btn-gold mt-2">Get Early Access</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[var(--tm-border)] bg-[var(--tm-off)]">
        <div className="tm-container py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <LogoLockup />
            <p className="mt-4 text-sm text-[#3B424B] leading-relaxed">Inventory & pricing intelligence for independent tire dealers.</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#3B424B] mb-3">Product</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#how" className="hover:text-[#14624A]">How It Works</a></li>
              <li><a href="#capabilities" className="hover:text-[#14624A]">Capabilities</a></li>
              <li><a href="#pricing" className="hover:text-[#14624A]">Pricing</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#3B424B] mb-3">Get Started</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/early-access" className="hover:text-[#14624A]">Request Early Access</Link></li>
              <li><Link to="/signin" className="hover:text-[#14624A]">Sign In</Link></li>
              <li><Link to="/signup" className="hover:text-[#14624A]">Create Account</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#3B424B] mb-3">Legal</div>
            <p className="text-xs text-[#3B424B] leading-relaxed">TreadMargin is an inventory analytics tool. It is not a POS, CRM, or ecommerce system. Recommendations are decision support — dealers remain in control of pricing and purchasing.</p>
          </div>
        </div>
        <div className="border-t border-[var(--tm-border)]">
          <div className="tm-container py-5 text-xs text-[#3B424B]">© {new Date().getFullYear()} TreadMargin. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
